/**
 * THE MOMENT AFTER SOMEBODY PAYS.
 *
 * Tom bought SSi Family on staging on 2026-09-07. Paddle took £25 and emailed
 * him a confirmation. The app then showed him nothing for several minutes — the
 * Settings screen still offered him the Upgrade row — and a second attempt put
 * him in an endless spinner. His words: "its a shitty process, race condition,
 * looks like nothing has happened, then spins endlessly".
 *
 * This composable is the third state the app was missing: PAID, NOT YET LANDED.
 * It owns four things and nothing else.
 *
 *   1. ACKNOWLEDGE AT ONCE. The record is written from Paddle's own
 *      checkout.completed event — in the page, the instant the payment
 *      succeeds — so the acknowledgement does not wait on our webhook, on a
 *      redirect, or on anything that can be slow.
 *
 *      THAT INDEPENDENCE IS THE POINT, and the measurement is why. Tom's
 *      webhook was not slow: his subscription row was written 1.3 seconds
 *      after his card was billed, while he sat looking at the Upgrade row for
 *      several minutes. The entitlement was there the whole time and the
 *      client never asked again — the success redirect out of Paddle's inline
 *      frame is the only thing that would have made it ask, and the parent
 *      page plainly never reloaded. checkout.completed fires in the PARENT
 *      window whatever the iframe does, so this no longer depends on that
 *      redirect, or on a reload, happening at all. While it stands, the
 *      not-subscribed state is covered and the buy path is shut.
 *   2. RESOLVE, DON'T SPIN. It polls /api/subscription on a decaying cadence
 *      and reports honestly which phase it is in, so the screen above it can
 *      show real progress rather than a spinner with no end.
 *   3. A DEFINITE END. On arrival it lands the buyer in the thing they bought.
 *      If it has not arrived, it says something true and calm rather than
 *      spinning forever, and it keeps the payment reference to hand.
 *   4. SURVIVE A RELOAD. Every bit of that is driven off the localStorage
 *      record in checkout/pendingPurchase.ts, so a refresh — or a phone killing
 *      a backgrounded PWA — comes back to the waiting state and not to the
 *      not-subscribed state.
 *
 * Module-level state, like useCheckout next door, so the one overlay reflects
 * one truth no matter which surface is mounted.
 */

import { ref, computed, readonly, watch } from 'vue'
import {
  readPendingPurchase,
  savePendingPurchase,
  clearPendingPurchase,
  type PendingPurchase,
} from '@/checkout/pendingPurchase'
import { useSharedSubscription } from './useSubscription'
import { openFamilyModal } from './useFamilyModal'
import { useVoiceEntitlement } from './useVoiceEntitlement'
import { useSharedUserEntitlements } from './useUserEntitlements'
import type { UserEntitlement } from '@ssi/core'
import { invalidateSignedInBoot } from '../playback/signedInBoot'
import { holdAuthModal, releaseAuthModal } from './useAuthModal'

/**
 * When the wording changes from "this takes a few seconds" to "this is taking
 * longer than usual".
 *
 * MEASURED, not guessed (2026-09-08, live Paddle + live DB, n=50 notifications
 * over 2026-07-03 to 2026-09-07): Paddle delivers a webhook in a median of
 * 1.06s, p90 2.36s, max 3.37s, with 50 of 50 delivered first time and no
 * retries. On Tom's own £25 Family purchase the subscription row was written
 * 1.3 seconds after his card was billed. So this is roughly seven times the
 * worst delivery ever observed — long enough that nobody is told a normal wait
 * is abnormal, short enough to speak up before they start to worry.
 */
export const SLOW_AFTER_MS = 25_000

/**
 * When we stop claiming it is imminent and hand them a way to reach a human.
 *
 * Deliberately generous against the measurement above — at 10 minutes we are
 * some 180x past the worst observed delivery, so anything still unresolved here
 * is not a slow webhook but a genuine fault, and a genuine fault deserves a
 * person rather than another spinner. The point of the number is that the
 * screen has a definite end, not that it gives up early.
 */
export const STALLED_AFTER_MS = 10 * 60_000

/**
 * How often to ask. Tight while the answer is likely imminent, then decaying,
 * because a poll every two seconds for ten minutes is 300 requests for one
 * £25 purchase and the later ones are the least likely to be the one that
 * answers.
 */
function pollDelayFor(elapsedMs: number): number {
  if (elapsedMs < 30_000) return 2_000
  if (elapsedMs < 2 * 60_000) return 5_000
  return 15_000
}

export type PendingPhase = 'confirming' | 'slow' | 'stalled' | 'arrived'

const record = ref<PendingPurchase | null>(readPendingPurchase())
const elapsedMs = ref(record.value ? Date.now() - record.value.paidAt : 0)
const arrived = ref(false)
/**
 * The buyer chose to carry on using the app rather than watch the wait.
 *
 * IT HIDES THE OVERLAY AND NOTHING ELSE. The purchase is still in flight, so
 * the buy path stays shut and the Settings screen still says the plan is being
 * switched on — stepping out of the waiting room must never reopen the door to
 * paying twice. In memory only, deliberately: a reload is exactly the moment
 * Tom's requirement says must land back in the waiting state.
 */
const dismissed = ref(false)
/** True while a poll is in flight — drives the "checking" beat in the UI so the
 *  progress a buyer sees is real activity rather than a decorative animation. */
const checking = ref(false)

/**
 * A PAID-FOR PURCHASE THAT HAS NOT LANDED SHUTS THE GENERIC SIGN-IN MODAL (job
 * #373). The buyer has just proved their address and paid; the session and the
 * entitlement are still settling, and during that window the app can read as
 * signed out for a beat. The waiting screen is the one steady thing to show —
 * the English "Sign in or create account" modal must not race over it. Held
 * from module load, so a reload mid-wait is covered from the first frame, and
 * released only when the buyer steps out of the waiting room or it lands.
 */
watch(
  [record, dismissed],
  ([r, d]) => { if (r !== null && !d) holdAuthModal('pending-purchase'); else releaseAuthModal('pending-purchase') },
  { immediate: true },
)

let tickTimer: ReturnType<typeof setInterval> | null = null
let pollTimer: ReturnType<typeof setTimeout> | null = null

function stopTimers(): void {
  if (tickTimer) { clearInterval(tickTimer); tickTimer = null }
  if (pollTimer) { clearTimeout(pollTimer); pollTimer = null }
}

/** The subscription is live. Stop waiting and let the overlay show the good news. */
function markArrived(): void {
  stopTimers()
  arrived.value = true
  // Good news outranks having been dismissed — bring the screen back to say so
  // and to land them in what they bought.
  dismissed.value = false
}

/**
 * Does any active entitlement already cover the course this purchase is for?
 * A full grant covers everything; a courses grant covers the codes it names.
 */
export function entitlementsCoverCourse(
  list: readonly UserEntitlement[] | null | undefined,
  courseCode: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!courseCode) return false
  for (const e of list ?? []) {
    if (e.expiresAt && new Date(e.expiresAt) <= now) continue
    if (e.accessType === 'full') return true
    if (e.accessType === 'courses' && (e.grantedCourses ?? []).includes(courseCode)) return true
  }
  return false
}

/**
 * THE OVERLAY CLEARS ITSELF ON SIGHT. Tom, staging 2026-10-01 18:14 BST: the
 * app held his eng_for_hin entitlement and still said "Your plan has not
 * appeared yet", because the only thing that could end the wait was a poll —
 * and a poll can be late (15s on a reloaded marker), hung (an unanswered
 * session or fetch), or never started (resume waits on the boot's own
 * entitlement fetch). So the wait no longer depends on any of it: whenever the
 * app's own entitlements cover the pending course, by whatever route they
 * arrived, the purchase has landed.
 */
let coverWatch = false
function watchForCover(): void {
  if (coverWatch) return
  coverWatch = true
  const ents = useSharedUserEntitlements()
  watch(
    [() => ents.entitlements.value, record],
    () => {
      const r = record.value
      if (r?.plan === 'course' && !arrived.value && entitlementsCoverCourse(ents.entitlements.value, r.courseCode)) markArrived()
    },
    { immediate: true },
  )
}

/**
 * A poll that never settles must not freeze the wait: `checking` would stay
 * true, "Check again" would refuse, and no later poll would be scheduled. Every
 * await inside (session read, shared refresh, direct fetch) is covered by this
 * one bound; a poll that times out simply told us nothing.
 */
export const POLL_TIMEOUT_MS = 12_000

async function pollOnce(): Promise<boolean> {
  const purchase = record.value
  let timer: ReturnType<typeof setTimeout> | null = null
  try {
    const confirmed = await Promise.race([
      pollOnceUnbounded(),
      new Promise<boolean>((resolve) => { timer = setTimeout(() => resolve(false), POLL_TIMEOUT_MS) }),
    ])
    // A completed purchase's poll cannot confirm a different purchase begun meanwhile.
    return record.value === purchase && confirmed
  } finally {
    if (timer) clearTimeout(timer)
    checking.value = false
  }
}

async function pollOnceUnbounded(): Promise<boolean> {
  // THE SIGN-IN BOOT PREDATES THE PURCHASE. bootFetch answers GET
  // /api/subscription and /api/entitlement/user from the snapshot taken at
  // sign-in, and a GET is never "spent", so every poll below would read the
  // pre-payment answer until the snapshot's TTL ran out (Tom, staging
  // 2026-10-01: the webhook had written his course entitlement, the screen
  // never saw it). Paying changes those answers; the boot is dead to us.
  invalidateSignedInBoot()
  // A VOICE purchase never writes a subscriptions row (job #490): it lands as
  // one entitlement row, so the thing to poll is the voice flag itself, or
  // this wait would never end.
  if (record.value?.plan === 'voice') {
    const voice = useVoiceEntitlement()
    checking.value = true
    try {
      return await voice.refresh()
    } catch {
      return false
    } finally {
      checking.value = false
    }
  }
  // A COURSE purchase (job #831) is one entitlement row too, naming its
  // course: poll the account's entitlements until that course is among them.
  if (record.value?.plan === 'course') {
    const want = record.value.courseCode
    const ents = useSharedUserEntitlements()
    checking.value = true
    try {
      if (entitlementsCoverCourse(ents.entitlements.value, want)) return true
      // Arrived means the APP holds the course: useEntitlement unlocks it and
      // PlanPicker stops selling it, and both read this shared list. A bare
      // GET that saw the row ended the wait with the course still locked
      // (lane review #183), so success goes through the shared refresh only.
      // The boot snapshot is invalidated above, and pollOnce's 12s bound
      // covers a refresh that hangs: the next poll asks again.
      await ents.refresh()
      return entitlementsCoverCourse(ents.entitlements.value, want)
    } catch {
      return false
    } finally {
      checking.value = false
    }
  }
  const sub = useSharedSubscription()
  checking.value = true
  try {
    await sub.refresh()
  } catch {
    // refresh() swallows its own errors; a failed poll is just a poll that
    // told us nothing. Never surface a network blip as a failed purchase.
  } finally {
    checking.value = false
  }
  return sub.isSubscribed.value
}

function schedulePoll(): void {
  if (pollTimer) return
  const delay = pollDelayFor(elapsedMs.value)
  pollTimer = setTimeout(async () => {
    pollTimer = null
    if (!record.value || arrived.value) return
    if (await pollOnce()) {
      markArrived()
      return
    }
    if (record.value && !arrived.value) schedulePoll()
  }, delay)
}

function startTimers(): void {
  if (!record.value || arrived.value) return
  if (!tickTimer) {
    tickTimer = setInterval(() => {
      if (!record.value) return
      elapsedMs.value = Date.now() - record.value.paidAt
    }, 1_000)
  }
  schedulePoll()
}

/**
 * The money has been taken. Called from Paddle's checkout.completed event, and
 * again — harmlessly — from the success landing, because on any given purchase
 * either one of those may be the only signal we get.
 */
export function beginPendingPurchase(input: {
  plan: 'premium' | 'family' | 'voice' | 'course'
  billingPeriod: 'monthly' | 'quarterly' | 'annual'
  transactionId?: string | null
  courseCode?: string | null
}): void {
  savePendingPurchase({
    plan: input.plan,
    billingPeriod: input.billingPeriod,
    courseCode: input.courseCode ?? null,
    transactionId: input.transactionId ?? null,
  })
  const stored = readPendingPurchase()
  if (!stored) return
  record.value = stored
  arrived.value = false
  dismissed.value = false
  elapsedMs.value = Date.now() - stored.paidAt
  watchForCover()
  if (arrived.value) return
  startTimers()
  // Ask immediately as well as on the schedule: the webhook may already have
  // landed by the time a redirect finishes, and a buyer should never sit
  // through a first delay we could have skipped.
  void (async () => { if (await pollOnce()) markArrived() })()
}

/**
 * Boot-time pick-up. Two jobs, both about surviving things that destroy memory:
 *
 *   • a record written before the Paddle success redirect is still on disk, so
 *     re-arm its timers and carry on waiting;
 *   • a success landing (?just_subscribed=1) that arrives with NO record —
 *     because checkout.completed never reached us — still gets one, inferred
 *     from the redirect Paddle was given. Belt and braces on the one moment
 *     that must not be missed.
 */
export function resumePendingPurchase(search: string = window.location.search): void {
  const existing = readPendingPurchase()
  if (existing) {
    record.value = existing
    // A reload lands back in the waiting state — that is the requirement, and
    // it is why "carry on without waiting" is memory-only.
    dismissed.value = false
    elapsedMs.value = Date.now() - existing.paidAt
    if (existing.plan !== 'voice' && existing.plan !== 'course' && useSharedSubscription().isSubscribed.value) {
      // It landed while we were away. Nothing to wait for. (A voice record
      // is answered by the poll below — the subscription says nothing about it.)
      markArrived()
      return
    }
    watchForCover()
    if (arrived.value) return
    startTimers()
    // Ask now. A reloaded marker is past its tight cadence, so the first
    // scheduled poll would be 15 seconds out — on a screen saying the plan
    // has not appeared, for a plan that is already there.
    void (async () => { if (record.value && !arrived.value && await pollOnce()) markArrived() })()
    return
  }
  try {
    const params = new URLSearchParams(search)
    if (params.get('just_subscribed') !== '1') return
    const voice = params.get('voice') === '1'
    const bought = params.get('bought')
    if (bought) {
      beginPendingPurchase({ plan: 'course', billingPeriod: 'monthly', transactionId: null, courseCode: bought })
      return
    }
    if (!voice && useSharedSubscription().isSubscribed.value) return
    beginPendingPurchase({
      plan: voice ? 'voice' : params.get('family') === '1' ? 'family' : 'premium',
      billingPeriod: 'monthly',
      transactionId: null,
    })
  } catch {
    // A URL we cannot parse is not a purchase. Nothing to do.
  }
}

/** Ask again, now. The one control on the stalled screen — so the end of the
 *  wait is still the buyer's to push on, rather than a dead page. */
export async function checkAgain(): Promise<void> {
  if (!record.value || checking.value) return
  if (await pollOnce()) markArrived()
}

/**
 * The end of the wait. Clears the record and puts the buyer in the thing they
 * bought — for Family that is the screen where they add their family, which is
 * the second half of what Tom hit when six seats appeared with nowhere to use
 * them.
 */
export function landPendingPurchase(): void {
  const plan = record.value?.plan
  stopTimers()
  clearPendingPurchase()
  record.value = null
  arrived.value = false
  dismissed.value = false
  elapsedMs.value = 0
  if (plan === 'family') openFamilyModal()
}

/**
 * Step out of the waiting room. The purchase carries on settling behind them
 * and the app carries on knowing about it — this only takes the overlay down.
 */
export function dismissPendingPurchase(): void {
  dismissed.value = true
}

export function usePendingPurchase() {
  watchForCover()
  const phase = computed<PendingPhase>(() => {
    if (arrived.value) return 'arrived'
    if (elapsedMs.value >= STALLED_AFTER_MS) return 'stalled'
    if (elapsedMs.value >= SLOW_AFTER_MS) return 'slow'
    return 'confirming'
  })

  return {
    /** The purchase in flight, or null. */
    pending: readonly(record),
    /** True whenever a purchase is paid for and not yet resolved — the state
     *  in which the app must never render the not-subscribed UI or offer to
     *  sell the thing that has just been bought. Unaffected by dismissing the
     *  overlay: stepping out of the waiting room does not un-buy anything. */
    isPending: computed(() => record.value !== null),
    /** Should the waiting overlay be on screen right now? */
    showOverlay: computed(() => record.value !== null && !dismissed.value),
    phase,
    elapsedMs: readonly(elapsedMs),
    checking: readonly(checking),
    begin: beginPendingPurchase,
    resume: resumePendingPurchase,
    checkAgain,
    land: landPendingPurchase,
    dismiss: dismissPendingPurchase,
  }
}
